/**
 * Manufacturing Data Loader Hook
 *
 * Manages loading of manufacturing data in the background with error handling and retry logic.
 * This data is loaded asynchronously without blocking the app initialization.
 * RTK Query handles caching, so data will be available when components need it.
 *
 * Shows persistent warning notifications when endpoints fail to load.
 * After 3 retry attempts, shows "Contact Support" message instead of retry button.
 *
 * @module useManufacturingDataLoader
 */

import { useCallback, useEffect, useRef } from 'react'
import { Close } from '@mui/icons-material'
import { Button, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'

import { useLazyGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useLazyGetMaterialsQuery } from '@/app/services/materials'
import { useLazyGetRatesQuery } from '@/app/services/rates'
import { useLazyGetRateTablesQuery } from '@/app/services/rateTables'
import { useLazyGetSheetsQuery } from '@/app/services/sheets'
import { SUPPORT_EMAIL } from '@/common/utils'

const RETRY_STORAGE_KEY = 'manufacturingDataRetryCount'
const MAX_RETRIES = 3

/**
 * Get current retry count from sessionStorage.
 *
 * @returns {number} Current retry count (0 if not set)
 */
const getRetryCount = () => {
    const count = sessionStorage.getItem(RETRY_STORAGE_KEY)
    return count ? parseInt(count, 10) : 0
}

/**
 * Increment retry count in sessionStorage.
 *
 * @returns {number} New retry count after increment
 */
const incrementRetryCount = () => {
    const count = getRetryCount() + 1
    sessionStorage.setItem(RETRY_STORAGE_KEY, count.toString())
    return count
}

/**
 * Reset retry count in sessionStorage.
 */
const resetRetryCount = () => {
    sessionStorage.removeItem(RETRY_STORAGE_KEY)
}

/**
 * Manufacturing data loader hook for background data fetching with error recovery.
 *
 * Loads manufacturing data in the background without blocking app initialization:
 * - Cutting technologies
 * - Materials
 * - Sheets
 * - Rate tables
 * - Rates
 *
 * All data is fetched in parallel and cached by RTK Query.
 * Errors trigger persistent notifications with retry capability.
 * After 3 failed retry attempts, shows "Contact Support" action.
 *
 * @returns {{
 *   loadManufacturingData: Function,
 *   retryManufacturingData: Function
 * }} Manufacturing data loading functions
 *
 * @example
 * const { loadManufacturingData, retryManufacturingData } = useManufacturingDataLoader()
 *
 * // Load data for organization
 * loadManufacturingData(organisationId)
 *
 * // Retry after failure
 * retryManufacturingData(organisationId)
 */
export const useManufacturingDataLoader = () => {
    const { closeSnackbar, enqueueSnackbar } = useSnackbar()
    const [getCuttingTechs] = useLazyGetCuttingTechnologiesQuery()
    const [getMaterials] = useLazyGetMaterialsQuery()
    const [getSheets] = useLazyGetSheetsQuery()
    const [getRateTables] = useLazyGetRateTablesQuery()
    const [getRates] = useLazyGetRatesQuery()

    // Ref to break circular dependency between loadManufacturingData and retryManufacturingData
    const loadManufacturingDataRef = useRef(null)

    /**
     * Load all manufacturing data in the background.
     *
     * This function fires off all API calls in parallel and returns immediately.
     * RTK Query will cache the results, making them available to components.
     * Errors are logged and trigger user notifications with retry capability.
     *
     * @param {string} organisationId - Organization ID to load data for
     */
    const loadManufacturingData = useCallback(
        (organisationId) => {
            console.debug('[Manufacturing Data] Starting background loading for org:', organisationId)

            let hasErrors = false
            const failedEndpoints = []

            // Fire all requests in parallel - RTK Query handles caching
            Promise.all([
                getCuttingTechs({ organisationId })
                    .unwrap()
                    .then(() => {
                        console.debug('[Manufacturing Data] Cutting technologies loaded')
                    })
                    .catch((error) => {
                        hasErrors = true
                        failedEndpoints.push('cutting technologies')
                        console.warn('[Manufacturing Data] Failed to load cutting technologies:', error)
                    }),

                getMaterials({ organisationId })
                    .unwrap()
                    .then(() => {
                        console.debug('[Manufacturing Data] Materials loaded')
                    })
                    .catch((error) => {
                        hasErrors = true
                        failedEndpoints.push('materials')
                        console.warn('[Manufacturing Data] Failed to load materials:', error)
                    }),

                getSheets({ organisationId })
                    .unwrap()
                    .then(() => {
                        console.debug('[Manufacturing Data] Sheets loaded')
                    })
                    .catch((error) => {
                        hasErrors = true
                        failedEndpoints.push('sheets')
                        console.warn('[Manufacturing Data] Failed to load sheets:', error)
                    }),

                getRateTables({ organisationId })
                    .unwrap()
                    .then(() => {
                        console.debug('[Manufacturing Data] Rate tables loaded')
                    })
                    .catch((error) => {
                        hasErrors = true
                        failedEndpoints.push('rate tables')
                        console.warn('[Manufacturing Data] Failed to load rate tables:', error)
                    }),

                getRates({ organisationId })
                    .unwrap()
                    .then(() => {
                        console.debug('[Manufacturing Data] Rates loaded')
                    })
                    .catch((error) => {
                        hasErrors = true
                        failedEndpoints.push('rates')
                        console.warn('[Manufacturing Data] Failed to load rates:', error)
                    }),
            ]).then(() => {
                console.debug('[Manufacturing Data] All background loading complete')

                if (hasErrors) {
                    const retryCount = getRetryCount()

                    if (retryCount >= MAX_RETRIES) {
                        // Max retries reached - show Contact Support
                        enqueueSnackbar(
                            'Manufacturing data failed to load after multiple attempts. Please contact support.',
                            {
                                variant: 'error',
                                persist: true,
                                preventDuplicate: true,
                                action: (snackbarId) => (
                                    <>
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                window.open(`mailto:${SUPPORT_EMAIL}`, '_blank')
                                            }}
                                        >
                                            CONTACT SUPPORT
                                        </Button>
                                        <IconButton
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                closeSnackbar(snackbarId)
                                                resetRetryCount()
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </>
                                ),
                            }
                        )
                    } else {
                        // Show retry option with inline retry logic to avoid circular dependency
                        enqueueSnackbar('Some manufacturing data failed to load. Features may be limited.', {
                            variant: 'warning',
                            persist: true,
                            preventDuplicate: true,
                            action: (snackbarId) => (
                                <>
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            closeSnackbar(snackbarId)
                                            const count = incrementRetryCount()
                                            console.debug(`[Manufacturing Data] Retry attempt ${count}/${MAX_RETRIES}`)
                                            loadManufacturingDataRef.current(organisationId)
                                        }}
                                    >
                                        RETRY
                                    </Button>
                                    <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            closeSnackbar(snackbarId)
                                            resetRetryCount()
                                        }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </>
                            ),
                        })
                    }
                } else {
                    // Success - reset retry counter
                    resetRetryCount()
                }
            })
        },
        [getCuttingTechs, getMaterials, getSheets, getRateTables, getRates, enqueueSnackbar, closeSnackbar]
    )

    // Store the latest loadManufacturingData in ref to break circular dependency
    // Must be done in useEffect to avoid accessing ref during render
    useEffect(() => {
        loadManufacturingDataRef.current = loadManufacturingData
    }, [loadManufacturingData])

    /**
     * Retry loading manufacturing data after a failure.
     *
     * Increments the retry counter and calls loadManufacturingData again.
     * After 3 retries, the notification will show "Contact Support" instead.
     *
     * @param {string} organisationId - Organization ID to load data for
     */
    const retryManufacturingData = useCallback((organisationId) => {
        const count = incrementRetryCount()
        console.debug(`[Manufacturing Data] Retry attempt ${count}/${MAX_RETRIES}`)
        loadManufacturingDataRef.current(organisationId)
    }, [])

    return {
        loadManufacturingData,
        retryManufacturingData,
    }
}
